import React, { memo, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { SwiperSlide } from "swiper/react";
import { Backdrop } from "@mui/material";
import { image } from "src/theme";

import {
  CommonYLine,
  modalOverHeaderZIndex,
} from "@assets/styles/CommonStyles";
import { commonSwiperOptions } from "@components/layout/CommonSwiperSettings";
import { GradientOverlay } from "@components/item/Overlays";
import { Image } from "@components/item/ImageRelated";
import { DangerouslyHtml } from "@components/item";
import { useGetHomeSlider } from "@utils/api-requests/GlobalRequests";
import {
  ReserveNowText,
  ViewDetailsText,
  ViewFullVideoText,
} from "@utils/lanugauge-settings/static-texts/TranslatedTexts";

// swiper styling
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/grid";
import "swiper/css/scrollbar";

import {
  MainBannerContainer,
  MainBannerIFrameWrapper,
  MainBannerIFrame,
  MainBannerIFrameClose,
  MainBannerSwiper,
  MainBannerViewMoreButton,
  MainPageHeroImage,
  MainBannerContent,
  BannerNumbering,
  BannerCategory,
  BannerMeta,
  BannerButtons,
  MainBannerArea,
  BannerTitle,
  BannerNavButtons,
  BannerNavButton,
} from "./HomeStyling";
import { MainBannerBookingBanner } from ".";
import { replaceAmpersand } from "@utils/modify-data/modifyData";

const MainBanner = () => {
  const isDesktop = useMediaQuery({ minWidth: 1200 });
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const swiperRef = useRef(null);
  const videoRef = useRef(null);

  const [currentVideoSrc, setCurrentVideoSrc] = useState("");
  const [openVideo, setOpenVideo] = useState(false);

  const { data: homeSlider } = useGetHomeSlider();

  const handleClose = () => {
    setOpenVideo(false);
    setCurrentVideoSrc("");
  };

  const handleOpen = (videoSrc) => {
    setCurrentVideoSrc(videoSrc);
    setOpenVideo(true);
  };

  const custom = {
    ...commonSwiperOptions,
    spaceBetween: 0,
    loop: homeSlider?.length > 1,
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
    },
    speed: 1000,
  };

  const handlePrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const checkIfVideo = (filePath) => {
    const fileExtension = filePath?.split(".").pop().toLowerCase();
    const videoExtensions = ["mp3", "mp4"];
    return videoExtensions.includes(fileExtension);
  };

  const resetActiveVideo = () => {
    if (swiperRef.current) {
      const activeSlide =
        swiperRef.current.swiper.slides[swiperRef.current.swiper.activeIndex];
      const video = activeSlide?.querySelector("video");
      const swiper = swiperRef.current.swiper;

      if (video) {
        video.currentTime = 0;
        setCurrentVideoSrc("");
        swiper.autoplay.stop(); // 비디오를 만나면 auto swipe 중지
      } else {
        swiper.autoplay.start(); // 비디오가 아닌 상태에 있을시 auto swipe start
      }
    }
  };

  // pauseOnMouseEnter 옵션을 사용하지 않고 직접 제어 (슬라이드가 넘어가는 도중에 hover시 paused되는 문제)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // 마우스 진입 및 이탈시 autoplay 상태 조정
  useEffect(() => {
    if (!swiperRef.current) return;

    const swiper = swiperRef.current.swiper;

    const handleMouseEnter = () => {
      if (swiper.autoplay.running) {
        swiper.autoplay.stop();
        setIsAutoPlaying(false);
      }
    };

    const handleMouseLeave = () => {
      if (!isAutoPlaying) {
        // 현재 진행 중인 트랜지션이 있다면 기다립니다
        if (swiper.animating) {
          swiper.once("transitionEnd", () => {
            swiper.autoplay.start();
            setIsAutoPlaying(true);
          });
        } else {
          swiper.autoplay.start();
          setIsAutoPlaying(true);
        }
      }
    };

    const swiperEl = swiper?.el;
    swiperEl?.addEventListener("mouseenter", handleMouseEnter);
    swiperEl?.addEventListener("mouseleave", handleMouseLeave);

    // cleanup
    return () => {
      swiperEl?.removeEventListener("mouseenter", handleMouseEnter);
      swiperEl?.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [isAutoPlaying]);

  // 컴포넌트 마운트 시 초기 비디오 체크
  useEffect(() => {
    if (swiperRef.current && homeSlider?.length > 0) {
      const firstSlide = swiperRef.current.swiper.slides[0];
      const firstVideo = firstSlide?.querySelector("video");
      const swiper = swiperRef.current.swiper;

      if (firstVideo) {
        firstVideo.currentTime = 0;
        setCurrentVideoSrc("");
        swiper.autoplay.stop();
      }
    }
  }, [homeSlider]);

  return (
    <MainBannerContainer>
      <Backdrop
        sx={{ color: "#fff", zIndex: modalOverHeaderZIndex }}
        open={openVideo}
        onClick={handleClose}
      >
        <MainBannerIFrameWrapper>
          <MainBannerIFrameClose type="button">
            <img
              src={image.modalCloseWhiteIcon.default}
              alt="Close Modal"
              width={isDesktop ? 40 : 32}
              height={isDesktop ? 40 : 32}
              loading="lazy"
              onClick={handleClose}
            />
          </MainBannerIFrameClose>

          <MainBannerIFrame
            title="vimeo-player"
            src={currentVideoSrc}
            frameborder="0"
            scrolling="no"
            allow="fullscreen; picture-in-picture"
            allowfullscreen
          />
        </MainBannerIFrameWrapper>
      </Backdrop>

      <MainBannerSwiper
        {...custom}
        ref={swiperRef}
        onSlideChange={() => resetActiveVideo()}
      >
        {homeSlider?.length > 1 && (
          <BannerNavButtons>
            <BannerNavButton left onClick={handlePrev}>
              <img
                src={image.chevronLeft.default}
                alt="left icon"
                draggable={false}
              />
            </BannerNavButton>

            <BannerNavButton onClick={handleNext}>
              <img
                src={image.chevronRight.default}
                alt="right icon"
                draggable={false}
              />
            </BannerNavButton>
          </BannerNavButtons>
        )}

        {homeSlider?.map((slider, idx) => {
          // Video First
          const thumbnail = isMobile
            ? slider.field_slider_thumbnail_mobile ||
              slider.field_image?.[0]?.src
            : slider.field_slider_thumbnail || slider.field_thumbnail?.[0]?.src;

          const thumbnailAlt = isMobile
            ? slider.field_image?.[0]?.alt ?? slider?.field_slider_title
            : slider.field_thumbnail?.[0]?.src ?? slider?.field_slider_title;

          // default image
          const isVideo = checkIfVideo(thumbnail);

          return (
            <SwiperSlide key={idx}>
              <MainPageHeroImage>
                <GradientOverlay opacity="0.4" />
                {isVideo ? (
                  <video
                    ref={videoRef}
                    preload="auto"
                    autoPlay
                    loop
                    playsInline
                    muted
                    volume="0"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  >
                    <source src={thumbnail} type="video/mp4" />
                  </video>
                ) : (
                  <Image src={thumbnail} alt={thumbnailAlt} loading="eager" />
                )}

                <MainBannerContent hasSpacing={homeSlider?.length > 1}>
                  <MainBannerArea>
                    <BannerNumbering active>
                      {idx + 1}
                      {` / ${homeSlider?.length || 1}`}
                    </BannerNumbering>

                    <div>
                      <BannerCategory>
                        <DangerouslyHtml value={slider?.field_offer_type} />
                      </BannerCategory>

                      <BannerTitle
                        hasSpacing={homeSlider?.length > 1}
                        className="ellipsis-3"
                        value={slider?.field_slider_title}
                      />
                    </div>

                    <BannerMeta>
                      {slider?.field_slide_period && (
                        <div>
                          <DangerouslyHtml value={slider?.field_slide_period} />
                        </div>
                      )}

                      {!isMobile &&
                        slider?.field_slide_period &&
                        slider?.field_slider_price && (
                          <CommonYLine width="0.2rem" height="2rem" />
                        )}

                      <DangerouslyHtml value={slider?.field_slider_price} />
                    </BannerMeta>

                    <BannerButtons>
                      {slider?.field_button_link_detail && (
                        <button
                          // onClick={() =>
                          //   navigate(
                          //     `/${lang}${slider.field_button_link_detail}`
                          //   )
                          // }
                          onClick={() =>
                            window.open(
                              replaceAmpersand(
                                slider?.field_button_link_detail,
                              ),
                              slider?.field_open_in_new_tab_detail
                                ? "_blank"
                                : "_self",
                            )
                          }
                        >
                          <ViewDetailsText />
                        </button>
                      )}

                      {slider?.field_button_link_reserve && (
                        <button
                          onClick={() =>
                            window.open(
                              replaceAmpersand(
                                slider?.field_button_link_reserve,
                              ),
                              slider?.field_open_in_new_tab_reserve
                                ? "_blank"
                                : "_self",
                            )
                          }
                        >
                          <ReserveNowText />
                        </button>
                      )}

                      <MainBannerViewMoreButton
                        isBottom
                        onClick={() =>
                          handleOpen(
                            slider.field_slider_video_full || thumbnail,
                          )
                        }
                        type="button"
                      >
                        {slider.field_show_video_button && isVideo && (
                          <ViewFullVideoText />
                        )}
                      </MainBannerViewMoreButton>
                    </BannerButtons>
                  </MainBannerArea>
                </MainBannerContent>
              </MainPageHeroImage>
            </SwiperSlide>
          );
        })}
      </MainBannerSwiper>

      <MainBannerBookingBanner />
    </MainBannerContainer>
  );
};

export default memo(MainBanner);
